import { Badge, BadgeProps } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type EscalationType = 
  | "HOSTING_PROVIDER" 
  | "REGISTRAR" 
  | "WEBMASTER" 
  | "CDN_PROVIDER" 
  | "DNS_PROVIDER" 
  | "DOMAIN_OWNER"
  | "OTHER"
  | string;

export interface EscalationTypeBadgeProps extends Omit<BadgeProps, "variant"> {
  type: EscalationType;
  /**
   * Children to display (overrides the type label)
   */
  children?: React.ReactNode;
}

/**
 * EscalationTypeBadge component for displaying escalation types with consistent color-coding.
 * Each type has its own color scheme for easy visual identification.
 */
export function EscalationTypeBadge({ type, children, className, ...props }: EscalationTypeBadgeProps) {
  const lowerType = type.toLowerCase();
  
  // Determine color classes based on escalation type
  let colorClasses = "";
  
  if (lowerType.includes("hosting")) {
    colorClasses = "bg-blue-100 text-blue-800 border-blue-300";
  } else if (lowerType.includes("registrar")) {
    colorClasses = "bg-purple-100 text-purple-800 border-purple-300";
  } else if (lowerType.includes("webmaster")) {
    colorClasses = "bg-green-100 text-green-800 border-green-300";
  } else if (lowerType.includes("cdn")) {
    colorClasses = "bg-orange-100 text-orange-800 border-orange-300";
  } else if (lowerType.includes("dns")) {
    colorClasses = "bg-pink-100 text-pink-800 border-pink-300";
  } else {
    colorClasses = "bg-gray-100 text-gray-800 border-gray-300";
  }
  
  // Format display text - children takes priority, then formatted type
  const displayText = children || formatEscalationType(type);
  
  return (
    <Badge
      variant="outline"
      className={cn(
        "px-1.5 py-0.5 text-xs font-medium rounded-lg",
        colorClasses,
        className
      )}
      {...props}
    >
      {displayText}
    </Badge>
  );
}

/**
 * Format escalation type for display (replace underscores with spaces, title case)
 */
function formatEscalationType(type: EscalationType): string {
  if (!type) return "Other";
  return type
    .replace(/_/g, " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

